import logger from '../../app/logger';
import { useNavigateExtended } from '../../shared/hooks/navigate.hooks';
import { Page } from '../../shared/components/layout/Page';
import { SummaryPageContent, SummaryFormState } from '../summary/SummaryPageContent';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { recreateConfiguration } from '../widget/thunks/basic/recreateConfiguration';
import { App } from 'antd';
import { useLogikForm } from '../../shared/logik/hooks/form.hooks';
import { publishEventAndRedirect } from '../../shared/utils/location.utils';
import { useCallback } from 'react';
import { savePreownedMasterConfiguration } from './thunks/savePreownedMasterConfiguration';
import {
    PreownedMasterEditEvent,
    PreownedMasterEditEventParams,
    PreownedMasterNewEvent,
    PreownedMasterNewEventParams,
} from './preowned.utils';

export function PreownedSummaryPage(): JSX.Element {
    const navigate = useNavigateExtended();
    const dispatch = useAppDispatch();
    const [form] = useLogikForm<SummaryFormState>();
    const { modal } = App.useApp();
    const siteId = useAppSelector((state) => state.widget.siteId);
    const serialNumber = useAppSelector((state) => state.preowned.details?.serialNumber);

    const launchObjectId = useAppSelector((state) => state.widget.init?.launchObjectId);
    const masterConfigurationId = useAppSelector((state) => state.widget.masterConfigurationId);

    const onFinish = useCallback(
        async (values: unknown) => {
            try {
                const res = await dispatch(savePreownedMasterConfiguration()).unwrap();
                modal.confirm({
                    title: 'Preowned has been successfully saved',
                    content: 'Now you can close the window or go back and make some changes',
                    icon: <></>,
                    okText: 'Save and continue',
                    cancelText: 'Save and close',
                    okButtonProps: { size: 'small' },
                    cancelButtonProps: { size: 'small' },
                    onOk: async () => await dispatch(recreateConfiguration(res.configuration.uuid)),
                    onCancel: async () => {
                        if (serialNumber) {
                            if (!masterConfigurationId) {
                                await publishEventAndRedirect<PreownedMasterNewEventParams>(PreownedMasterNewEvent, {
                                    launchObjectId,
                                    siteId,
                                    serial: serialNumber,
                                });
                            } else {
                                await publishEventAndRedirect<PreownedMasterEditEventParams>(PreownedMasterEditEvent, {
                                    launchObjectId,
                                    siteId,
                                    serial: serialNumber,
                                });
                            }
                        }
                    },
                });
            } catch (err: unknown) {
                logger.error(err);
            }
        },
        [dispatch, launchObjectId, masterConfigurationId, modal, serialNumber, siteId],
    );

    return (
        <Page
            navProps={{
                onClickBack: () => navigate('..', { preserveSearch: true }),
                onClickNext: () => form.submit(),
                nextButtonTitle: 'Finish',
                hideSaveButton: true,
            }}>
            <SummaryPageContent onFinish={onFinish} form={form} />
        </Page>
    );
}
